import React, {useCallback, useContext} from 'react';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {useFormContext} from 'react-hook-form';
import FormErrorMessage from '@src/components/Form/FormErrorMessage';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import useFormValue from '@src/hooks/useFormValue';
import classnames from '@src/helpers/classnames';
import Switch from '@src/components/Fields/Switch';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import FormattedPrice from '@src/components/FormattedPrice/FormattedPrice';
import {TotalPriceIndependentField} from '@views/orders/fields/products/TotalPriceField';

export default function FrozePriceField({rowIndex: index, isDisabled, className}: {
    rowIndex: number,
    isDisabled?: boolean,
    className?: string,
}) {
    const {
        fieldName,
        forceDisabled,
        forceReadonly
    } = useContext<IFormFieldItemPropsContext<IOrderModel> | null>(FormFieldItemPropsContext) ?? {};

    const name = `${fieldName}.${index}.is_price_frozen`;
    const label = 'OrderForm.products.fields.is_price_frozen';

    const {setValue} = useFormContext();
    const {currentValue: frozenPrice} = useFormValue(`${fieldName}.${index}.saved_price`);
    const {currentValue} = useFormValue(name);
    const [design] = useContext(FormFieldDesignContext);

    const checked = currentValue === true;
    const canFrozePrice = usePermissionGranted('orders@can-freeze-order-product-price');
    const canUnFrozePrice = usePermissionGranted('orders@can-unfreeze-order-product-price');

    const onChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        setValue(name, e.currentTarget.checked);
    }, [name, setValue]);

    if (!checked && !canFrozePrice) {
        return null;
    }

    const savedPriceValueContainerClassName = classnames('text-secondary ms-auto ps-3', {
        'd-none': !checked,
    });

    return <div className={classnames(className, 'd-flex w-100 rounded border p-2', {
        'border-info': checked,
    })}>
        <div>
            <Switch
                    id={name}
                    label={label}
                    checked={checked}
                    onChange={onChange}
                    readOnly={checked && !canUnFrozePrice || !checked && canUnFrozePrice || design.fieldsAreReadOnly || forceReadonly}
                    disabled={isDisabled || design.fieldsAreDisabled || forceDisabled}
                    className="mb-0"
            />

            <FormErrorMessage fieldName={name}/>
        </div>
        {checked && !frozenPrice ? (
                <TotalPriceIndependentField rowIndex={index} className={savedPriceValueContainerClassName}/>
        ) : (
                <div className={savedPriceValueContainerClassName}>
                    <FormattedPrice amount={frozenPrice / 100}/>
                </div>
        )}
    </div>;
}
